import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { StaticClass } from 'src/components/shared-components/class/general-classes/static-classes';
import { LogLevel } from 'src/components/shared-components/enum/enumLog';
import { DateTimeStringFomat } from 'src/components/shared-components/enum/enumMisc';
import { DatePickerEmission } from 'src/model/interface/datePickeremission';
import { LoggerService } from 'src/service/logger.service';

@Component({
    selector: 'app-form-date-picker',
    templateUrl: './form-date-picker.component.html',
    styleUrls: ['./from-date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormDatePickerComponent implements OnInit {
  private _logContext: string = "Date-picker.component";

  private _initDatePicker: string = "2000-01-01";
  private _initDateInput: string = "";
  private _initTime: string = "00:00";
  private _jump: number = 100;

  public toolBarColor: string = "primary";
  public formTimeDatePicker: UntypedFormGroup = null;

  public lblundo: string = "Annulla";
  public lblConfirm: string = "Ok";

  /**
   * Input property; if true, the component shall include a "ion-datetime" element with "date" presentation.
   */
  @Input() isDate: boolean;

  /**
   * Input property; if true, the component shall include a "ion-datetime" element with "time" presentation.
   */
  @Input() isTime: boolean;

  /**
   * Input property; this enum value specify the peculiar format in which  the "initDate" is expressed. If not specified, the default is  "YYYY-MM-DD".
   */
  @Input() formatDate: DateTimeStringFomat;

  /**
   * Input property; this enum value specify the peculiar format in which  the "initTime" is expressed. If not specified, the default is  "HH:mm".
   */
  @Input() formatTime: DateTimeStringFomat;

  /**
   *Input property; String-date used to initialise the component.
   */
  @Input() initDate: string;

  /**
   * Input property; String-time used to initialise the component.
   */
  @Input() initTime: string;

  /**
   * Input property; if set to true; the component shall also include a text-type input field through which the user shall be able to insert
   * date-strings and time-strings.
   */
  @Input() isInputFieldVisible: boolean;

  /**
   * Input property; String-date used to set the maximum value of the ion-datetime
   */
  @Input() maxDate: string;

  /**
   * Input property; String-date used to set the minimum value of the ion-datetime
   */
  @Input() minDate: string;

  /**
   * This event  emits a string, detailing the date/time selected in the component, to the father.
   * The strings shall be formatted following the same pattern as specified in "formatDate"/"formatTime" input properties.
   */
  @Output() onDateTimeSubmission = new EventEmitter<DatePickerEmission>();

  constructor(
    private _loggerService: LoggerService,
    private _fb: UntypedFormBuilder
  ) { }

  async ngOnInit() {
    if (this.minDate == "") {
      this.minDate = this.setDefaultDate(1);
    }
    if (this.maxDate == "") {
      this.maxDate = this.setDefaultDate(2);
    }
    this.initDatePicker();

    await this.initializeForm();

    this.subscribeValueFormChange();
  }

  /**
   * This method initialise the "ion-datetime" element in the component with the values passed throught the "initDate" and "initTime" input properties.
   * ALSO, it converts the initialisation strings to the inner date(YYYY-MM-DD) and time (HH:mm) formats.
   */
  private initDatePicker(): void {

    this.initDateFields();

    this.initTimeFields();

    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "initDatePicker()", "Init date picker: " + this._initDatePicker + " | Init date input: " + this._initDateInput + "| Init time: " + this._initTime);
  }

  /**
   * This method define the initialisation strings to date-related formControls; it does so converting the "initDate" string from the used formatting (as specified
   * in "formatDate" input property) to the internal specific one (YYYY-MM-DD).
   */
  private initDateFields() {

    if (this.isDate && this.initDate) {
      switch (this.formatDate) {
        case DateTimeStringFomat.SHORT_DATE_STRAIGHT:
          this._initDatePicker = StaticClass.formatDateReverse(this.initDate);
          this._initDateInput = this.initDate;
          break;

        case DateTimeStringFomat.SHORT_DATE_INVERSE:
          this._initDatePicker = this.initDate;
          this._initDateInput = StaticClass.formatDate(this.initDate);

          break;

        default:
          this._initDatePicker = this.initDate;
          this._initDateInput = StaticClass.formatDate(this.initDate);
          break;
      }
    } else {
      //fallback
      this._initDatePicker = "2022-01-01";
      this._initDateInput = StaticClass.formatDate(this._initDatePicker);
    }

  }

  /**
   * This method define the initialisation strings to time-related formControls; it does so converting the "initTime" string from the used formatting (as specified
   * in "formatTime" input property) to the internal specific one (HH:mm).
   */
  private initTimeFields() {

    if (this.isTime && this.initTime) {
      switch (this.formatTime) {
        case DateTimeStringFomat.LONG_HOUR_LONG_MINUTES:
          this._initTime = this.initTime;
          break;

        default:
          this._initTime = this.initTime;
          break;
      }
    } else {
      //fallback
      this._initTime = "00:00";
    }

  }

  private async initializeForm(): Promise<void> {

    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "initializeForm()", "_initDatePicker:" + this._initDatePicker);

    if (this.isDate) {
      this.formTimeDatePicker = this._fb.group(
        {
          datePickerCnt: new UntypedFormControl(this._initDatePicker),
          dateInput: new UntypedFormControl(this._initDateInput)
        }
      );

    }

    if (this.isTime) {

      this.formTimeDatePicker = this._fb.group(
        {
          timePickerCnt: new UntypedFormControl(this._initTime),
          timeInput: new UntypedFormControl(this._initTime)
        }
      );
    }

  }

  private subscribeValueFormChange(): void {
    //#region React to change to start date datePicker
    if (this.isDate) {
      this.formTimeDatePicker
        .get("datePickerCnt")
        .valueChanges.subscribe(async (newStartDateFromPicker: string) => {

          if (this.isInputFieldVisible) {
            this.formTimeDatePicker.patchValue(
              {
                dateInput: StaticClass.formatDateReverse(newStartDateFromPicker),
              },
              {
                emitEvent: false,
              }
            );
          }

        });
    }
    //#endregion

    //#region React to change to start date datePicker
    if (this.isTime) {
      this.formTimeDatePicker
        .get("timePickerCnt")
        .valueChanges.subscribe(async (newStartTimeFromPicker: string) => {

          if (this.isInputFieldVisible) {
            this.formTimeDatePicker.patchValue(
              {
                timeInput: newStartTimeFromPicker,
              },
              {
                emitEvent: false,
              }
            );
          }

        });
    }
    //#endregion


    //#region Emit to father-element      
    this.formTimeDatePicker.valueChanges.pipe(debounceTime(200)).subscribe(async () => {
      this.onDateTimeSubmission.emit(this.prepareEmission());
    });
    //#endregion
  }

  //#region Prepare emission to father-element
  /**
   * This method incapsulate date/time value (as read from the "datePickerCnt"/"timePickerCnt" formControls) into a "DatePickerEmission" object in order to be returned to father-component.
   * @returns Object of "DatePickerEmission"-type containing retun-data; these shall be properly re-formatted to align to input-informat.
   */
  private prepareEmission(): DatePickerEmission {

    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "prepareEmission()", "datePicker value: " + this.formTimeDatePicker.value.datePickerCnt);
    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "prepareEmission()", "timePicker value: " + this.formTimeDatePicker.value.timePickerCnt);

    return {
      dateString: this.formatDateOutput(this.formTimeDatePicker.value.datePickerCnt),
      timeString: this.formatTimeOutput(this.formTimeDatePicker.value.timePickerCnt)
    };

  }

  /**
   * This method refactor (if needed) the date-string to be passed to the father-element by emission of "DatePickerEmission" object.
   * The refactoring is done taking in account the external date format passed by "formatDate" input property.
   * @param internalDateString Date string formatted according to the internally used pattern (YYYY-MM-DD).
   * @returns Formatted output date string. IF THE DATE-RELATED FORM CONTROL HAS NOT BEEN INITIALISED, it is null.
   */
  private formatDateOutput(internalDateString: string): string {
    let dateOutputString: string = (internalDateString ??= null);

    if (dateOutputString) {
      switch (this.formatDate) {
        case DateTimeStringFomat.SHORT_DATE_STRAIGHT:
          dateOutputString = StaticClass.formatDate(dateOutputString);

          break;
        case DateTimeStringFomat.SHORT_DATE_INVERSE:
          dateOutputString = dateOutputString;

          break;

      }
    }

    return dateOutputString;
  }


  /**
 * This method refactor (if needed) the time-string to be passed to the father-element by emission of "DatePickerEmission" object.
 * The refactoring is done taking in account the external time format passed by "formatTime" input property.
 * @param internalTimeString Time string formatted according to the internally used pattern (HH:mm).
 * @returns Formatted output time string. IF THE TIME-RELATED FORM CONTROL HAS NOT BEEN INITIALISED, it is null.
 */
  private formatTimeOutput(internalTimeString: string): string {
    let timeOutputString: string = (internalTimeString ??= null);

    if (timeOutputString) {
      switch (this.formatTime) {
        case DateTimeStringFomat.LONG_HOUR_LONG_MINUTES:
          timeOutputString = timeOutputString;
          break;
      }
    }

    return timeOutputString;
  }
  //#endregion

  private setDefaultDate(value: number): string {
    let date = new Date();
    if (value == 1) {
      date.setFullYear(date.getFullYear() - this._jump);
    } else if (value == 2) {
      date.setFullYear(date.getFullYear() + this._jump);
    }
    return date.toISOString();
  }

}
